import apiService from "@/core/services/apiService";

export type State = { classes: object };
import {Module, VuexModule, Mutation, Action} from "vuex-module-decorators";
import {Actions, Mutations} from "@/store/enums/store.enums";
import {BlogActions, BlogMutation} from "@/modules/blog/store/enums";
import store from "@/store";

@Module
export default class Store extends VuexModule {
    renderKey = 1;
    listParams = {
        page: 1,
        results: 10,
        search: null,
        sort: [],
        filter: {}
    };
    listCache = [];
    listTotal = 0;
    settings = [];
    topics = [];

    get getBlogRenderKey() {
        return 'blog-' + this.renderKey;
    }

    get getBlogLastListResult() {
        return this.listCache;
    }

    get getBlogLastListTotal() {
        return this.listTotal;
    }

    get getBlogSettings() {
        return this.settings;
    }

    get getBlogTopics() {
        return this.topics;
    }

    @Mutation
    [BlogMutation.SET_RENDER_UPDATE]() {
        this.renderKey++;
    }

    @Mutation
    [BlogMutation.SET_LIST_RESULT]({listParams, listTotal, listCache}) {
        this.listParams = listParams;
        this.listCache = listCache;
        this.listTotal = listTotal;
    }

    @Mutation
    [BlogMutation.SET_SETTINGS_RESULT](settings) {
        this.settings = settings;
    }

    @Mutation
    [BlogMutation.SET_TOPICS_RESULT](topics) {
        this.topics = topics;
    }

    @Action
    [BlogActions.REFRESH_LIST]() {
        this.context.dispatch(BlogActions.FETCH_LIST, this.listParams);
    }

    @Action
    [BlogActions.FETCH_LIST](params) {

        return new Promise<void>((resolve, reject) => {

            apiService.post("blog/list", params).then((response) => {

                const data = response.data || null;

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                // return results into storage for reactive update if needed //
                this.context.commit(BlogMutation.SET_LIST_RESULT, {
                    listParams: params,
                    listTotal: data.results,
                    listCache: data.result
                });
                resolve();

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [BlogActions.GET_ITEM](detailId) {

        return new Promise<void>((resolve, reject) => {

            apiService.get(`blog/get/${detailId}`).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                // return results to dispatcher without mutation //
                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [BlogActions.CREATE_ITEM](payload) {

        return new Promise((resolve, reject) => {

            const {data} = payload;

            apiService.post('blog/create/', data).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                // flag cached keep alive comp to render new //
                this.context.commit(BlogMutation.SET_RENDER_UPDATE);

                // refresh list in background//
                this.context.dispatch(BlogActions.REFRESH_LIST);

                // return results to dispatcher without mutation //
                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [BlogActions.UPDATE_ITEM](payload) {

        return new Promise((resolve, reject) => {

            const {editId, data} = payload;

            apiService.put(`blog/edit/${editId}`, data).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                // refresh list in background//
                this.context.dispatch(BlogActions.REFRESH_LIST);

                // return results to dispatcher without mutation //
                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [BlogActions.DELETE_ITEM](editId) {

        return new Promise((resolve, reject) => {

            apiService.delete(`blog/delete/${editId}`).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                // refresh list in background//
                this.context.dispatch(BlogActions.REFRESH_LIST);

                // return results to dispatcher without mutation //
                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [BlogActions.FETCH_SETTINGS]() {

        return new Promise<void>((resolve, reject) => {

            apiService.get("content/get-reference-settings/blog").then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.commit(BlogMutation.SET_SETTINGS_RESULT, data.result);
                resolve();

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [BlogActions.CREATE_SETTING](payload) {

        return new Promise((resolve, reject) => {

            apiService.post('content/create-reference-setting', payload).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.dispatch(BlogActions.FETCH_SETTINGS);
                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [BlogActions.UPDATE_SETTING](payload) {

        return new Promise((resolve, reject) => {

            const {settingsId, data: settingData} = payload;

            apiService.put(`content/update-reference-setting/${settingsId}`, settingData).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.dispatch(BlogActions.FETCH_SETTINGS);
                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [BlogActions.DELETE_SETTING](settingsId) {

        return new Promise((resolve, reject) => {

            apiService.delete(`content/delete-reference-setting/${settingsId}`).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.dispatch(BlogActions.FETCH_SETTINGS);
                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [BlogActions.FETCH_TOPICS]() {

        return new Promise<void>((resolve, reject) => {

            apiService.get("content/get-reference-topics/blog").then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.commit(BlogMutation.SET_TOPICS_RESULT, data.result);
                resolve();

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [BlogActions.CREATE_TOPIC](payload) {

        return new Promise((resolve, reject) => {

            apiService.post('content/create-reference-topic', payload).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.dispatch(BlogActions.FETCH_TOPICS);
                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [BlogActions.UPDATE_TOPIC](payload) {

        return new Promise((resolve, reject) => {

            const {topicId, data: topicData} = payload;

            apiService.put(`content/update-reference-topic/${topicId}`, topicData).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.dispatch(BlogActions.FETCH_TOPICS);
                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [BlogActions.DELETE_TOPIC](topicId) {

        return new Promise((resolve, reject) => {

            apiService.delete(`content/delete-reference-topic/${topicId}`).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                this.context.dispatch(BlogActions.FETCH_TOPICS);
                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

}
